// src/components/UserManagement.js - Enhanced User Management with User Details & Edit
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  AccountTree,
  Search,
  Refresh,
  CheckCircle,
  Warning,
  Person,
  ExpandMore,
  ExpandLess,
  People,
  TrendingUp,
  MonetizationOn,
  Close,
  Email,
  Phone,
  CalendarToday,
  AccountBalance,
  Edit,
  Save,
  Cancel,
  Visibility,
  Info,
  AttachMoney,
  ShoppingCart,
  Group,
  Settings,
  Link,
  Download,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { fetchReferralTreeOptimized } from "../services/ReferralTreeService";
import { AddressService } from "../services/AddressService";
import * as XLSX from "xlsx";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import { calculateCesEligibilityFromReferralTree } from "../config/cashOutAffiliateEligibilityConfig";

const UserManagement = ({ users, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [treeDialogOpen, setTreeDialogOpen] = useState(false);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [referralData, setReferralData] = useState({});
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [treeLoading, setTreeLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [calculatedReferralCounts, setCalculatedReferralCounts] = useState({});
  const [userLevels, setUserLevels] = useState({}); // Store CES levels for each user
  const [exportLoading, setExportLoading] = useState(false);

  // Calculate actual referral count for a user
  const calculateActualReferralCount = useCallback(async (userId) => {
    try {
      const referralsQuery = query(
        collection(db, "referrals"),
        where("referrerId", "==", userId)
      );
      const referralsSnapshot = await getDocs(referralsQuery);
      
      // Use Set to deduplicate by referredUserId
      const uniqueReferredUsers = new Set();
      referralsSnapshot.docs.forEach(doc => {
        const referralData = doc.data();
        uniqueReferredUsers.add(referralData.referredUserId);
      });
      
      return uniqueReferredUsers.size;
    } catch (error) {
      console.error("Error calculating referral count:", error);
      return 0;
    }
  }, []);

  // Count Ultra Members from referral tree
  const countUltraMembers = (referralTree = []) => {
    let count = 0;
    const countRecursive = (refs) => {
      refs.forEach((referral) => {
        if (referral.referredUserData?.hasPurchasedPackage === true) {
          count++;
        }
        if (referral.subReferrals && referral.subReferrals.length > 0) {
          countRecursive(referral.subReferrals);
        }
      });
    };
    countRecursive(referralTree);
    return count;
  };

  // Count Direct Referrals (Level 1 only) - ONLY Ultra Members
  const countDirectReferrals = (referralTree = []) => {
    return referralTree.filter(
      (referral) => referral.referredUserData?.hasPurchasedPackage === true
    ).length;
  };

  // Calculate CES level for a user
  const calculateUserLevel = useCallback(async (userId) => {
    try {
      const tree = await fetchReferralTreeOptimized(userId);
      const eligibility = calculateCesEligibilityFromReferralTree(tree);
      const level = eligibility.currentLevel || 0;
      return level;
    } catch (error) {
      console.error(`Error calculating level for user ${userId}:`, error);
      return 0;
    }
  }, []);

  // Calculate referral counts when users change
  useEffect(() => {
    const calculateAllReferralCounts = async () => {
      if (!users || users.length === 0) return;
      
      const counts = {};
      const levels = {};
      
      // Calculate counts and levels in batches to avoid overwhelming the system
      const batchSize = 5;
      for (let i = 0; i < users.length; i += batchSize) {
        const batch = users.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (user) => {
            const count = await calculateActualReferralCount(user.uid);
            counts[user.uid] = count;
            
            // Calculate level (this might be slow, so we do it in batches)
            const level = await calculateUserLevel(user.uid);
            levels[user.uid] = level;
          })
        );
      }
      
      setCalculatedReferralCounts(counts);
      setUserLevels(levels);
    };

    calculateAllReferralCounts();
  }, [users, calculateActualReferralCount, calculateUserLevel]);

  // Export users to Excel
  const handleExportToExcel = async () => {
    try {
      setExportLoading(true);
      
      // Fetch all user data including addresses
      const exportData = await Promise.all(
        users.map(async (user) => {
          // Fetch user addresses
          const addressResult = await AddressService.getUserAddresses(user.uid);
          const addresses = addressResult.success ? addressResult.data : [];
          const primaryAddress = addresses.find(addr => addr.isPrimary) || addresses[0] || null;
          
          // Format address string
          const formatAddress = (addr) => {
            if (!addr) return "";
            const parts = [
              addr.addressLine1,
              addr.addressLine2,
              addr.city,
              addr.state,
              addr.postalCode || addr.pincode
            ].filter(Boolean);
            return parts.join(", ");
          };
          
          // Get user level
          const level = userLevels[user.uid] !== undefined 
            ? userLevels[user.uid] 
            : (user.highestCesLevelAchieved || 0);
          
          // Format registration fee status
          const regFeePaid = user.registrationFee?.paid || false;
          const regFeeConfirmed = user.registrationFee?.confirmedByAdmin || false;
          const regFeeStatus = regFeeConfirmed 
            ? "Confirmed" 
            : regFeePaid 
            ? "Paid (Pending)" 
            : "Not Paid";
          
          return {
            "User ID": user.uid || "",
            "Name": user.name || "",
            "Email": user.email || "",
            "Phone": user.phoneNumber || "",
            "Referral ID": user.myReferralId || "",
            "Referred By": user.referredBy || "",
            "Referral ID Used": user.referralIdUsed || "",
            "Total Referrals": calculatedReferralCounts[user.uid] ?? user.totalReferrals ?? 0,
            "CES Level": level || 0,
            "Status": user.hasPurchasedPackage ? "Package Buyer" : "New User",
            "Points Eligible": user.hasPurchasedPackage ? "Yes" : "No",
            "Cashback Balance": user.cashbackBalance || 0,
            "Cashback Earned": user.cashbackEarned || 0,
            "Cashback Used": user.cashbackUsed || 0,
            "Commission Balance": user.commissionBalance || user.confirmedCommissions || 0,
            "Commission Earned": user.commissionEarned || user.totalCommissions || 0,
            "Commission Used": user.commissionUsed || 0,
            "CES Coins": user.cesCoins || 0,
            "Registration Fee": user.registrationFee?.amount || 499,
            "Registration Fee Status": regFeeStatus,
            "Registration Fee Paid At": user.registrationFee?.paidAt 
              ? new Date(user.registrationFee.paidAt).toLocaleString() 
              : "",
            "Registration Fee Confirmed At": user.registrationFee?.confirmedAt 
              ? new Date(user.registrationFee.confirmedAt).toLocaleString() 
              : "",
            "Old Address": user.address || "",
            "Old Pincode": user.pincode || "",
            "Primary Address Full Name": primaryAddress?.fullName || "",
            "Primary Address Line 1": primaryAddress?.addressLine1 || "",
            "Primary Address Line 2": primaryAddress?.addressLine2 || "",
            "Primary Address City": primaryAddress?.city || "",
            "Primary Address State": primaryAddress?.state || "",
            "Primary Address Postal Code": primaryAddress?.postalCode || primaryAddress?.pincode || "",
            "Primary Address Phone": primaryAddress?.phoneNumber || "",
            "Primary Address Label": primaryAddress?.label || "",
            "Total Addresses": addresses.length,
            "All Addresses": addresses.map(addr => formatAddress(addr)).join(" | "),
            "Joined Date": user.createdAt 
              ? new Date(user.createdAt).toLocaleString() 
              : "",
            "Last Updated": user.updatedAt 
              ? new Date(user.updatedAt).toLocaleString() 
              : "",
            "Photo URL": user.photoURL || "",
          };
        })
      );
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      const colWidths = [
        { wch: 30 }, // User ID
        { wch: 25 }, // Name
        { wch: 30 }, // Email
        { wch: 15 }, // Phone
        { wch: 15 }, // Referral ID
        { wch: 30 }, // Referred By
        { wch: 20 }, // Referral ID Used
        { wch: 15 }, // Total Referrals
        { wch: 12 }, // CES Level
        { wch: 15 }, // Status
        { wch: 15 }, // Points Eligible
        { wch: 15 }, // Cashback Balance
        { wch: 15 }, // Cashback Earned
        { wch: 15 }, // Cashback Used
        { wch: 18 }, // Commission Balance
        { wch: 18 }, // Commission Earned
        { wch: 18 }, // Commission Used
        { wch: 12 }, // CES Coins
        { wch: 18 }, // Registration Fee
        { wch: 25 }, // Registration Fee Status
        { wch: 25 }, // Registration Fee Paid At
        { wch: 30 }, // Registration Fee Confirmed At
        { wch: 30 }, // Old Address
        { wch: 12 }, // Old Pincode
        { wch: 20 }, // Primary Address Full Name
        { wch: 30 }, // Primary Address Line 1
        { wch: 30 }, // Primary Address Line 2
        { wch: 20 }, // Primary Address City
        { wch: 20 }, // Primary Address State
        { wch: 15 }, // Primary Address Postal Code
        { wch: 15 }, // Primary Address Phone
        { wch: 15 }, // Primary Address Label
        { wch: 15 }, // Total Addresses
        { wch: 50 }, // All Addresses
        { wch: 25 }, // Joined Date
        { wch: 25 }, // Last Updated
        { wch: 40 }, // Photo URL
      ];
      ws['!cols'] = colWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `InfinityWhale_Users_Export_${timestamp}.xlsx`;
      
      // Write file
      XLSX.writeFile(wb, filename);
      
      setExportLoading(false);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setExportLoading(false);
      alert("Failed to export users to Excel. Please try again.");
    }
  };

  // Glassmorphism styles

  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  const cardGlassStyles = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 48px rgba(11, 28, 72, 0.35)",
      background: "rgba(255, 255, 255, 0.25)",
    },
  };

  // Initialize edit form data
  const initializeEditForm = (user) => {
    setEditFormData({
      name: user.name || "",
      phoneNumber: user.phoneNumber || "",
      metaWhaleReferralLink: user.metaWhaleReferralLink || "",
      metaWhaleReceivingAddress: user.metaWhaleReceivingAddress || "",
      metaWhaleUplinerLink: user.metaWhaleUplinerLink || "",
      metaWhaleUplinerReceivedAddress:
        user.metaWhaleUplinerReceivedAddress || "",
      cashbackBalance: user.cashbackBalance || 0,
      cashbackEarned: user.cashbackEarned || 0,
      cashbackUsed: user.cashbackUsed || 0,
      commissionBalance: user.commissionBalance || 0,
      commissionEarned: user.commissionEarned || 0,
      commissionUsed: user.commissionUsed || 0,
      hasPurchasedPackage: user.hasPurchasedPackage || false,
      totalReferrals: user.totalReferrals || 0,
      registrationFeeConfirmed: user.registrationFee?.confirmedByAdmin || false,
      registrationFeePaid: user.registrationFee?.paid || false,
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Save user changes
  const handleSaveUser = async () => {
    try {
      setSaveLoading(true);

      // Find the user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", selectedUser.uid)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

        const updateData = {
          name: editFormData.name.trim(),
          phoneNumber: editFormData.phoneNumber.trim(),
          metaWhaleReferralLink: editFormData.metaWhaleReferralLink.trim(),
          metaWhaleReceivingAddress:
            editFormData.metaWhaleReceivingAddress.trim(),
          metaWhaleUplinerLink: editFormData.metaWhaleUplinerLink.trim(),
          metaWhaleUplinerReceivedAddress:
            editFormData.metaWhaleUplinerReceivedAddress.trim(),
          cashbackBalance: Number(editFormData.cashbackBalance),
          cashbackEarned: Number(editFormData.cashbackEarned),
          cashbackUsed: Number(editFormData.cashbackUsed),
          commissionBalance: Number(editFormData.commissionBalance),
          commissionEarned: Number(editFormData.commissionEarned),
          commissionUsed: Number(editFormData.commissionUsed),
          hasPurchasedPackage: editFormData.hasPurchasedPackage,
          totalReferrals: Number(editFormData.totalReferrals),
          "registrationFee.confirmedByAdmin":
            editFormData.registrationFeeConfirmed,
          "registrationFee.paid": editFormData.registrationFeePaid,
          updatedAt: new Date().toISOString(),
        };

        await updateDoc(userDocRef, updateData);

        // Update selected user data
        setSelectedUser((prev) => ({
          ...prev,
          ...updateData,
          registrationFee: {
            ...prev.registrationFee,
            confirmedByAdmin: editFormData.registrationFeeConfirmed,
            paid: editFormData.registrationFeePaid,
          },
        }));

        setIsEditing(false);
        alert("User updated successfully!");
        onRefresh(); // Refresh the users list
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update user: " + error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle viewing user details
  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setUserDetailsOpen(true);
    setTabValue(0);
    setIsEditing(false);
    initializeEditForm(user);
  };

  // OPTIMIZED: Fetch referral tree using batch service
  const fetchReferralTree = async (userId, userEmail) => {
    try {
      setTreeLoading(true);
      console.log("Fetching referral tree for user:", userEmail);

      // Use optimized service - fetches ALL data in just 2 queries
      const tree = await fetchReferralTreeOptimized(userId);
      
      console.log(`Built referral tree with ${tree.length} direct referrals for ${userEmail}`);
      return tree;
    } catch (error) {
      console.error("Error fetching referral tree:", error);
      return [];
    } finally {
      setTreeLoading(false);
    }
  };

  // Count total referrals recursively
  const countTotalReferrals = (referrals) => {
    let count = referrals.length;
    for (const referral of referrals) {
      count += countTotalReferrals(referral.subReferrals || []);
    }
    return count;
  };

  // Handle viewing referral tree
  const handleViewReferralTree = async (user) => {
    setSelectedUser(user);
    setTreeDialogOpen(true);

    const tree = await fetchReferralTree(user.uid, user.email);
    setReferralData({ [user.uid]: tree });
  };

  // Toggle expanded state for tree nodes
  const toggleExpanded = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Render referral tree node
  const ReferralTreeNode = ({ referral, level = 0, parentId = "" }) => {
    const nodeId = `${parentId}-${referral.id}`;
    const hasSubReferrals =
      referral.subReferrals && referral.subReferrals.length > 0;
    const isExpanded = expandedNodes.has(nodeId);
    const userData = referral.referredUserData;

    return (
      <Box key={nodeId} sx={{ ml: level * 3 }}>
        <Card
          sx={{
            ...cardGlassStyles,
            mb: 1,
            bgcolor:
              level === 0
                ? "rgba(0, 114, 206, 0.1)"
                : "rgba(255, 255, 255, 0.1)",
            border:
              level === 0
                ? `2px solid ${primaryBlue[300]}`
                : "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <Avatar
                  src={userData?.photoURL}
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    border: `2px solid ${
                      level === 0 ? primaryBlue[500] : primaryBlue[300]
                    }`,
                  }}
                >
                  <Person />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: level === 0 ? primaryBlue[800] : primaryBlue[700],
                      mb: 0.5,
                    }}
                  >
                    {userData?.name || "Unknown User"}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: primaryBlue[600],
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Email sx={{ fontSize: 14, mr: 0.5 }} />
                      {userData?.email || "No email"}
                    </Typography>

                    {userData?.hasPurchasedPackage ? (
                      <Chip
                        label="Package Buyer"
                        size="small"
                        color="success"
                        icon={<CheckCircle />}
                      />
                    ) : (
                      <Chip
                        label="New User"
                        size="small"
                        color="default"
                        icon={<Warning />}
                      />
                    )}

                    <Chip
                      label={`Level ${level + 1}`}
                      size="small"
                      sx={{
                        bgcolor:
                          level === 0
                            ? primaryBlue[100]
                            : `rgba(${level * 40}, ${
                                150 + level * 20
                              }, 255, 0.2)`,
                        color:
                          level === 0 ? primaryBlue[800] : primaryBlue[700],
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 2 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: primaryBlue[600],
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 12, mr: 0.5 }} />
                      Referred:{" "}
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: primaryBlue[600],
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 12, mr: 0.5 }} />
                      Code: {referral.referralCode}
                    </Typography>

                    {hasSubReferrals && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#00BFFF",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <People sx={{ fontSize: 12, mr: 0.5 }} />
                        {referral.totalSubReferrals} sub-referrals
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {hasSubReferrals && (
                <IconButton
                  onClick={() => toggleExpanded(nodeId)}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.3)",
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.5)" },
                  }}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </Box>
          </CardContent>
        </Card>

        {hasSubReferrals && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{ ml: 2, borderLeft: `2px solid ${primaryBlue[200]}`, pl: 2 }}
            >
              {referral.subReferrals.map((subReferral) => (
                <ReferralTreeNode
                  key={`${nodeId}-${subReferral.referredUserId}`}
                  referral={subReferral}
                  level={level + 1}
                  parentId={nodeId}
                />
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.myReferralId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCommissionEligibilityChip = (user) => {
    if (user.hasPurchasedPackage) {
      return (
        <Tooltip title="User has purchased a package and can earn points">
          <Chip
            label="Points Eligible"
            size="small"
            color="success"
            icon={<CheckCircle />}
          />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="User must purchase a package to earn points">
          <Chip
            label="Package Required"
            size="small"
            color="warning"
            icon={<Warning />}
          />
        </Tooltip>
      );
    }
  };

  const getUserTypeChip = (user) => {
    if (user.hasPurchasedPackage) {
      return <Chip label="Package Buyer" size="small" color="primary" />;
    } else if (user.registrationFee?.confirmedByAdmin) {
      return <Chip label="Registered" size="small" color="info" />;
    } else if (user.registrationFee?.paid) {
      return <Chip label="Payment Pending" size="small" color="warning" />;
    } else {
      return <Chip label="New User" size="small" color="default" />;
    }
  };

  // Tab panel component
  const TabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`user-tabpanel-${index}`}
        aria-labelledby={`user-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ ...glassStyles, p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: primaryBlue[800], fontWeight: 700 }}
          >
            User Management
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportToExcel}
              disabled={exportLoading || loading || !users || users.length === 0}
              sx={{
                borderColor: "#00BFFF",
                color: "#00BFFF",
                "&:hover": {
                  borderColor: "#45a049",
                  bgcolor: "rgba(76, 175, 80, 0.08)",
                },
              }}
            >
              {exportLoading ? "Exporting..." : "Export to Excel"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={onRefresh}
              disabled={loading}
              sx={{
                borderColor: primaryBlue[300],
                color: primaryBlue[700],
                "&:hover": {
                  borderColor: primaryBlue[500],
                  bgcolor: "rgba(0, 114, 206, 0.08)",
                },
              }}
            >
              Refresh Users
            </Button>
          </Box>
        </Box>

        <Typography variant="body1" sx={{ color: primaryBlue[600], mb: 3 }}>
          Manage all registered users and view their referral networks
        </Typography>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search by name, email, or referral ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: primaryBlue[500] }} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: "rgba(255, 255, 255, 0.8)",
              borderRadius: 2,
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.9)",
              },
            },
          }}
        />
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(0, 114, 206, 0.1)" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <People sx={{ fontSize: 40, color: primaryBlue[600], mb: 1 }} />
              <Typography
                variant="h4"
                sx={{ color: primaryBlue[700], fontWeight: 700 }}
              >
                {users.length}
              </Typography>
              <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(76, 175, 80, 0.1)" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <CheckCircle sx={{ fontSize: 40, color: "#00BFFF", mb: 1 }} />
              <Typography
                variant="h4"
                sx={{ color: "#2e7d32", fontWeight: 700 }}
              >
                {users.filter((u) => u.hasPurchasedPackage).length}
              </Typography>
              <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                Package Buyers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(156, 39, 176, 0.1)" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <MonetizationOn sx={{ fontSize: 40, color: "#1E40AF", mb: 1 }} />
              <Typography
                variant="h4"
                sx={{ color: "#7b1fa2", fontWeight: 700 }}
              >
                {users.filter((u) => u.hasPurchasedPackage).length}
              </Typography>
              <Typography variant="body2" sx={{ color: "#7b1fa2" }}>
                Points Eligible
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Table */}
      <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.8)" }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    User
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    Contact
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    Level
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    Points
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    Referral Info
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    Joined
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm
                          ? "No users found matching your search"
                          : "No users found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        "&:hover": { bgcolor: "rgba(0, 114, 206, 0.04)" },
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            src={user.photoURL}
                            sx={{
                              width: 40,
                              height: 40,
                              border: `2px solid ${primaryBlue[200]}`,
                            }}
                          >
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: primaryBlue[800] }}
                            >
                              {user.serialNumber ? `#${user.serialNumber} - ` : ''}{user.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: primaryBlue[600] }}
                            >
                              ID: {user.myReferralId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Email
                              sx={{
                                fontSize: 14,
                                mr: 0.5,
                                color: primaryBlue[500],
                              }}
                            />
                            {user.email}
                          </Typography>
                          {user.phoneNumber && (
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                            >
                              <Phone
                                sx={{
                                  fontSize: 14,
                                  mr: 0.5,
                                  color: primaryBlue[500],
                                }}
                              />
                              {user.phoneNumber}
                            </Typography>
                          )}
                          {user.upiId && (
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", alignItems: "center", color: "#00BFFF", fontWeight: 500, mb: 0.5 }}
                            >
                              UPI: {user.upiId}
                            </Typography>
                          )}
                          {user.bankName && (
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", alignItems: "center", color: "#1E40AF", fontWeight: 500 }}
                            >
                              Bank: {user.bankName} | A/C: {user.accountNumber} | IFSC: {user.ifscCode}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>{getUserTypeChip(user)}</TableCell>

                      <TableCell>
                        {(() => {
                          // Use stored level from profile if available, otherwise use calculated level
                          const storedLevel = user.highestCesLevelAchieved || 0;
                          const calculatedLevel = userLevels[user.uid];
                          const displayLevel = calculatedLevel !== undefined ? calculatedLevel : storedLevel;
                          
                          if (calculatedLevel === undefined && storedLevel === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                Calculating...
                              </Typography>
                            );
                          }
                          
                          return (
                            <Chip
                              label={`Level ${displayLevel || 0}`}
                              size="small"
                              color={
                                displayLevel >= 8
                                  ? "success"
                                  : displayLevel >= 5
                                  ? "warning"
                                  : displayLevel >= 1
                                  ? "info"
                                  : "default"
                              }
                              sx={{
                                fontWeight: 600,
                                minWidth: 70,
                              }}
                            />
                          );
                        })()}
                      </TableCell>

                      <TableCell>
                        {getCommissionEligibilityChip(user)}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={`${calculatedReferralCounts[user.uid] ?? user.totalReferrals ?? 0} referrals`}
                            size="small"
                            color="info"
                            sx={{ width: "fit-content" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: primaryBlue[600] }}
                          >
                            Referral ID: {user.myReferralId}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[700] }}
                        >
                          {formatDate(user.createdAt)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View User Details">
                            <IconButton
                              onClick={() => handleViewUserDetails(user)}
                              sx={{
                                bgcolor: "rgba(76, 175, 80, 0.1)",
                                color: "#00BFFF",
                                "&:hover": {
                                  bgcolor: "rgba(76, 175, 80, 0.2)",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Referral Tree">
                            <IconButton
                              onClick={() => handleViewReferralTree(user)}
                              sx={{
                                bgcolor: "rgba(0, 114, 206, 0.1)",
                                color: primaryBlue[700],
                                "&:hover": {
                                  bgcolor: "rgba(0, 114, 206, 0.2)",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <AccountTree />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog
        open={userDetailsOpen}
        onClose={() => setUserDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...glassStyles,
            bgcolor: "rgba(255, 255, 255, 0.95)",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                src={selectedUser?.photoURL}
                sx={{ width: 48, height: 48, mr: 2 }}
              >
                <Person />
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: primaryBlue[800], fontWeight: 700 }}
                >
                  {selectedUser?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                  {selectedUser?.email} • ID: {selectedUser?.myReferralId}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {!isEditing ? (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setIsEditing(true)}
                  sx={{
                    borderColor: primaryBlue[300],
                    color: primaryBlue[700],
                  }}
                >
                  Edit User
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={() => setIsEditing(false)}
                    sx={{ borderColor: "#001A4D", color: "#001A4D" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveUser}
                    disabled={saveLoading}
                    sx={{
                      bgcolor: "#00BFFF",
                      "&:hover": { bgcolor: "#45a049" },
                    }}
                  >
                    {saveLoading ? <CircularProgress size={20} /> : "Save"}
                  </Button>
                </>
              )}
              <IconButton onClick={() => setUserDetailsOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={(event, newValue) => setTabValue(newValue)}
              aria-label="user details tabs"
            >
              <Tab
                label="Basic Info"
                icon={<Info />}
                iconPosition="start"
                id="user-tab-0"
                aria-controls="user-tabpanel-0"
              />
              <Tab
                label="Financial"
                icon={<AttachMoney />}
                iconPosition="start"
                id="user-tab-1"
                aria-controls="user-tabpanel-1"
              />
              <Tab
                label="Packages"
                icon={<ShoppingCart />}
                iconPosition="start"
                id="user-tab-2"
                aria-controls="user-tabpanel-2"
              />
              <Tab
                label="Referrals"
                icon={<Group />}
                iconPosition="start"
                id="user-tab-3"
                aria-controls="user-tabpanel-3"
              />
              <Tab
                label="Meta Whale"
                icon={<Link />}
                iconPosition="start"
                id="user-tab-4"
                aria-controls="user-tabpanel-4"
              />
            </Tabs>
          </Box>

          {/* Basic Info Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={
                    isEditing ? editFormData.name : selectedUser?.name || ""
                  }
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  disabled={!isEditing}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={selectedUser?.email || ""}
                  disabled
                  margin="normal"
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={
                    isEditing
                      ? editFormData.phoneNumber
                      : selectedUser?.phoneNumber || ""
                  }
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Referral ID"
                  value={selectedUser?.myReferralId || ""}
                  disabled
                  margin="normal"
                  helperText="Referral ID cannot be changed"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="User ID (UID)"
                  value={selectedUser?.uid || ""}
                  disabled
                  margin="normal"
                  helperText="System generated unique ID"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Referrals"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.totalReferrals
                      : selectedUser?.totalReferrals || 0
                  }
                  onChange={(e) =>
                    handleInputChange("totalReferrals", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Created At"
                  value={formatDate(selectedUser?.createdAt)}
                  disabled
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Updated"
                  value={formatDate(selectedUser?.updatedAt)}
                  disabled
                  margin="normal"
                />
              </Grid>

              {isEditing && (
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editFormData.hasPurchasedPackage}
                          onChange={(e) =>
                            handleInputChange(
                              "hasPurchasedPackage",
                              e.target.checked
                            )
                          }
                        />
                      }
                      label="Has Purchased Package"
                    />
                  </FormGroup>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Financial Tab */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: primaryBlue[800] }}
                >
                  Cashback Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Cashback Balance"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.cashbackBalance
                      : selectedUser?.cashbackBalance || 0
                  }
                  onChange={(e) =>
                    handleInputChange("cashbackBalance", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Cashback Earned"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.cashbackEarned
                      : selectedUser?.cashbackEarned || 0
                  }
                  onChange={(e) =>
                    handleInputChange("cashbackEarned", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Cashback Used"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.cashbackUsed
                      : selectedUser?.cashbackUsed || 0
                  }
                  onChange={(e) =>
                    handleInputChange("cashbackUsed", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: primaryBlue[800] }}
                >
                  Points Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Points Balance"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.commissionBalance
                      : selectedUser?.commissionBalance || 0
                  }
                  onChange={(e) =>
                    handleInputChange("commissionBalance", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Points Earned"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.commissionEarned
                      : selectedUser?.commissionEarned || 0
                  }
                  onChange={(e) =>
                    handleInputChange("commissionEarned", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Points Used"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.commissionUsed
                      : selectedUser?.commissionUsed || 0
                  }
                  onChange={(e) =>
                    handleInputChange("commissionUsed", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: primaryBlue[800] }}
                >
                  Registration Fee Status
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Registration Fee Amount"
                  value={`₹${selectedUser?.registrationFee?.amount || 0}`}
                  disabled
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mt: 2 }}>
                  {isEditing ? (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editFormData.registrationFeePaid}
                            onChange={(e) =>
                              handleInputChange(
                                "registrationFeePaid",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Registration Fee Paid"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editFormData.registrationFeeConfirmed}
                            onChange={(e) =>
                              handleInputChange(
                                "registrationFeeConfirmed",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Confirmed by Admin"
                      />
                    </FormGroup>
                  ) : (
                    <Box>
                      <Chip
                        label={
                          selectedUser?.registrationFee?.paid
                            ? "Paid"
                            : "Unpaid"
                        }
                        color={
                          selectedUser?.registrationFee?.paid
                            ? "success"
                            : "error"
                        }
                        sx={{ mr: 1, mb: 1 }}
                      />
                      <Chip
                        label={
                          selectedUser?.registrationFee?.confirmedByAdmin
                            ? "Admin Confirmed"
                            : "Pending Confirmation"
                        }
                        color={
                          selectedUser?.registrationFee?.confirmedByAdmin
                            ? "success"
                            : "warning"
                        }
                        sx={{ mb: 1 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Packages Tab */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              {selectedUser?.purchasedPackage ? (
                <>
                  <Grid item xs={12}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body1">
                        <strong>Package Status:</strong> User has purchased a
                        package and is eligible for points
                      </Typography>
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Package Name"
                      value={selectedUser.purchasedPackage.name || ""}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Package Category"
                      value={selectedUser.purchasedPackage.category || ""}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Package Price"
                      value={`₹${selectedUser.purchasedPackage.price || 0}`}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Order ID"
                      value={selectedUser.purchasedPackage.orderId || ""}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Purchased At"
                      value={formatDate(
                        selectedUser.purchasedPackage.purchasedAt
                      )}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Confirmed At"
                      value={formatDate(
                        selectedUser.purchasedPackage.confirmedAt
                      )}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Package Description"
                      value={selectedUser.purchasedPackage.description || ""}
                      disabled
                      multiline
                      rows={3}
                      margin="normal"
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body1">
                      This user has not purchased any packages yet. They need to
                      purchase a package to become eligible for points
                      earnings.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {selectedUser?.pendingPackagePurchase && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: primaryBlue[800] }}
                    >
                      Pending Package Purchase
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Pending Package Name"
                      value={
                        selectedUser.pendingPackagePurchase.packageName || ""
                      }
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Pending Amount"
                      value={`₹${
                        selectedUser.pendingPackagePurchase.amount || 0
                      }`}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Pending Order ID"
                      value={selectedUser.pendingPackagePurchase.orderId || ""}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Created At"
                      value={formatDate(
                        selectedUser.pendingPackagePurchase.createdAt
                      )}
                      disabled
                      margin="normal"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </TabPanel>

          {/* Referrals Tab */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ color: primaryBlue[800] }}>
                    Referral Information
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AccountTree />}
                    onClick={() => handleViewReferralTree(selectedUser)}
                  >
                    View Referral Tree
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: "center", p: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{ color: primaryBlue[700], fontWeight: 700 }}
                  >
                    {selectedUser?.totalReferrals || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                    Total Referrals Made
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: "center", p: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#00BFFF", fontWeight: 700 }}
                  >
                    {selectedUser?.myReferralId || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#00BFFF" }}>
                    My Referral ID
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ textAlign: "center", p: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{ color: "#D4AF37", fontWeight: 700 }}
                  >
                    {selectedUser?.purchasedPackage?.referralIdUsed || "None"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#D4AF37" }}>
                    Used Referral ID
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Meta Whale Tab */}
          <TabPanel value={tabValue} index={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: primaryBlue[800] }}
                >
                  Meta Whale Integration
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meta Whale Referral Link"
                  value={
                    isEditing
                      ? editFormData.metaWhaleReferralLink
                      : selectedUser?.metaWhaleReferralLink || ""
                  }
                  onChange={(e) =>
                    handleInputChange("metaWhaleReferralLink", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  helperText="User's Meta Whale referral link"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meta Whale Receiving Address"
                  value={
                    isEditing
                      ? editFormData.metaWhaleReceivingAddress
                      : selectedUser?.metaWhaleReceivingAddress || ""
                  }
                  onChange={(e) =>
                    handleInputChange(
                      "metaWhaleReceivingAddress",
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                  margin="normal"
                  multiline
                  rows={2}
                  helperText="Address where user receives Meta Whale payments"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meta Whale Upliner Link"
                  value={
                    isEditing
                      ? editFormData.metaWhaleUplinerLink
                      : selectedUser?.metaWhaleUplinerLink || ""
                  }
                  onChange={(e) =>
                    handleInputChange("metaWhaleUplinerLink", e.target.value)
                  }
                  disabled={!isEditing}
                  margin="normal"
                  helperText="User's upliner Meta Whale link"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meta Whale Upliner Received Address"
                  value={
                    isEditing
                      ? editFormData.metaWhaleUplinerReceivedAddress
                      : selectedUser?.metaWhaleUplinerReceivedAddress || ""
                  }
                  onChange={(e) =>
                    handleInputChange(
                      "metaWhaleUplinerReceivedAddress",
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                  margin="normal"
                  multiline
                  rows={2}
                  helperText="Address where upliner receives payments"
                />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
      </Dialog>

      {/* Referral Tree Dialog */}
      <Dialog
        open={treeDialogOpen}
        onClose={() => setTreeDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...glassStyles,
            bgcolor: "rgba(255, 255, 255, 0.95)",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AccountTree sx={{ mr: 2, color: primaryBlue[700] }} />
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: primaryBlue[800], fontWeight: 700 }}
                >
                  Referral Network Tree
                </Typography>
                <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                  {selectedUser?.name} ({selectedUser?.email})
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setTreeDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {treeLoading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress sx={{ color: primaryBlue[600] }} />
              <Typography
                variant="body1"
                sx={{ mt: 2, color: primaryBlue[700] }}
              >
                Loading referral tree...
              </Typography>
            </Box>
          ) : selectedUser && referralData[selectedUser.uid] ? (
            <>
              {referralData[selectedUser.uid].length === 0 ? (
                <Alert
                  severity="info"
                  sx={{ ...glassStyles, bgcolor: "rgba(0, 114, 206, 0.1)" }}
                >
                  <Typography variant="body1">
                    <strong>{selectedUser.name}</strong> hasn't referred anyone
                    yet.
                  </Typography>
                </Alert>
              ) : (
                <>
                  <Alert
                    severity="success"
                    sx={{
                      ...glassStyles,
                      bgcolor: "rgba(76, 175, 80, 0.1)",
                      mb: 3,
                    }}
                  >
                    <Typography variant="body1">
                      <strong>{selectedUser.name}</strong> has referred{" "}
                      <strong>{referralData[selectedUser.uid].length}</strong>{" "}
                      people directly, with{" "}
                      <strong>
                        {referralData[selectedUser.uid].reduce(
                          (sum, ref) => sum + ref.totalSubReferrals,
                          0
                        )}
                      </strong>{" "}
                      total sub-referrals in their network.
                    </Typography>
                  </Alert>

                  <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
                    {referralData[selectedUser.uid].map((referral) => (
                      <ReferralTreeNode
                        key={`root-${referral.referredUserId}`}
                        referral={referral}
                        level={0}
                        parentId="root"
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setTreeDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: primaryBlue[500],
              "&:hover": { bgcolor: primaryBlue[600] },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
